import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Api from './service';
import LoginPage from './modulos/login/LoginPage';
import HomePage from './modulos/home/HomePage';
import Designacoes from './modulos/designacoes/Designacoes';
import CadastrarPublicador from './modulos/publicadores/CadastrarPublicador';
import NotFound from './modulos/common/NotFound';
import UsuariosService from './service/usuario';
import GerenciarSenha from './modulos/admin/GerenciarSenha';
import SwaggerAdmin from './modulos/admin/SwaggerAdmin';
import Suporte from './modulos/suporte/Suporte';
import GerenciarPontos from './modulos/admin/GerenciarPontos';
import GerenciarPublicadores from './modulos/admin/GerenciarPublicadores';
import GerenciarArranjos from './modulos/admin/GerenciarArranjos';
import RelatorioDeUso from './modulos/admin/relatorios/RelatorioDeUso';
import ConsultarPublicadores from './modulos/publicadores/ConsultarPublicadores';
import { startTokenRefreshInterval } from './service/tokenRefresh';
import RegioesService from './service/regioes';
import Comunicacoes from './modulos/notificacoes/Comunicacoes';
import ComunicacoesEspeciais from './modulos/notificacoes/ComunicacoesEspeciais';
// Página "Minhas Notificações" desativada por enquanto — as notificações são lidas pelo sino do cabeçalho.
// import MinhasNotificacoes from './modulos/notificacoes/MinhasNotificacoes';
import FormComunicado from './modulos/comunicados/FormComunicado';
import ResultadosComunicado from './modulos/comunicados/ResultadosComunicado';
import Eventos from './modulos/eventos/Eventos';
import Experiencias from './modulos/experiencias/Experiencias';
import Chamados from './modulos/chamados/Chamados';
import ChamadoDetalhe from './modulos/chamados/ChamadoDetalhe';
import { CHAMADOS_ATIVO, EXPERIENCIAS_ATIVO } from './funcionalidades';

// ─── Guardas de rota ─────────────────────────────────────────────────────────

const ProtectedRoute = ({ children }) => {
  const id = localStorage.getItem('idUsuario');
  if (!id) return <Navigate to="/" replace />;
  return children;
};

const ProtectedAdminRoute = ({ children }) => {
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    const id = localStorage.getItem('idUsuario');
    if (!id) { setStatus('unauthenticated'); return; }
    UsuariosService.getUsuarioById(id)
      .then(u => {
        const perfArr = (u && Array.isArray(u.perfil)) ? u.perfil : (u && u.perfil ? [u.perfil] : []);
        try { localStorage.setItem('perfilUsuario', JSON.stringify(perfArr)); } catch (e) {}
        const temAcesso = perfArr.includes('admin') || perfArr.includes('coordenador-geral');
        setStatus(temAcesso ? 'ok' : 'denied');
      })
      .catch(() => setStatus('unauthenticated'));
  }, []);
  if (status === 'loading') return null;
  if (status === 'unauthenticated') return <Navigate to="/" replace />;
  if (status === 'denied') return <NotFound />;
  return children;
};

// Notificações: admin, coordenador-geral, coordenador-regional e coordenador-ponto
// (a própria tela ajusta as opções conforme o escopo do usuário)
const ProtectedNotificacoesRoute = ({ children }) => {
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    const id = localStorage.getItem('idUsuario');
    if (!id) { setStatus('unauthenticated'); return; }
    UsuariosService.getUsuarioById(id)
      .then(u => {
        const perfArr = (u && Array.isArray(u.perfil)) ? u.perfil : (u && u.perfil ? [u.perfil] : []);
        try { localStorage.setItem('perfilUsuario', JSON.stringify(perfArr)); } catch (e) {}
        const temAcesso = ['admin', 'coordenador-geral', 'coordenador-regional', 'coordenador-ponto']
          .some(p => perfArr.includes(p));
        setStatus(temAcesso ? 'ok' : 'denied');
      })
      .catch(() => setStatus('unauthenticated'));
  }, []);
  if (status === 'loading') return null;
  if (status === 'unauthenticated') return <Navigate to="/" replace />;
  if (status === 'denied') return <NotFound />;
  return children;
};

const ProtectedOnlyAdminRoute = ({ children }) => {
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    const id = localStorage.getItem('idUsuario');
    if (!id) { setStatus('unauthenticated'); return; }
    UsuariosService.getUsuarioById(id)
      .then(u => {
        const perfArr = (u && Array.isArray(u.perfil)) ? u.perfil : (u && u.perfil ? [u.perfil] : []);
        try { localStorage.setItem('perfilUsuario', JSON.stringify(perfArr)); } catch (e) {}
        setStatus(perfArr.includes('admin') ? 'ok' : 'denied');
      })
      .catch(() => setStatus('unauthenticated'));
  }, []);
  if (status === 'loading') return null;
  if (status === 'unauthenticated') return <Navigate to="/" replace />;
  if (status === 'denied') return <NotFound />;
  return children;
};

const ProtectedHomeRoute = ({ children }) => {
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    const id = localStorage.getItem('idUsuario');
    if (!id) { setStatus('ok'); return; }
    const perfisRaw = localStorage.getItem('perfilUsuario');
    let perfis = [];
    try { perfis = JSON.parse(perfisRaw) || []; } catch {}
    if (!perfis.includes('secretaria')) { setStatus('ok'); return; }
    import('./service').then(({ default: Api }) => {
      Api.get(`pontos/listarPontos?coordenadorId=${id}`)
        .then(resp => {
          const pontos = Array.isArray(resp.data) ? resp.data : [];
          const temEventos = pontos.some(p => String(p?.nome || '').trim().toLowerCase() === 'eventos');
          const temOutros  = pontos.some(p => String(p?.nome || '').trim().toLowerCase() !== 'eventos');
          if (temEventos && !temOutros) setStatus('redirect-eventos');
          else setStatus('ok');
        })
        .catch(() => setStatus('ok'));
    });
  }, []);
  if (status === 'loading') return null;
  if (status === 'redirect-eventos') return <Navigate to="/eventos" replace />;
  return children;
};

const ProtectedChamadosRoute = ({ children }) => {
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const id = localStorage.getItem('idUsuario');
    if (!id) {
      setStatus('unauthenticated');
      return;
    }

    const perfisRaw = localStorage.getItem('perfilUsuario');
    let perfis = [];
    try { perfis = JSON.parse(perfisRaw) || []; } catch {}
    const ehAdminOuGeral = perfis.some((p) => ['admin', 'coordenador-geral'].includes(p));
    if (ehAdminOuGeral) {
      setStatus('ok');
      return;
    }

    Api.get('/arranjos/manutencao-carrinhos/responsaveis')
      .then((resp) => {
        const lista = Array.isArray(resp.data) ? resp.data : [];
        const usuarioEhMembro = lista.some((item) => {
          const usuarioId = item?.usuario?._id || item?.usuario;
          return String(usuarioId) === String(id);
        });
        setStatus(usuarioEhMembro ? 'ok' : 'denied');
      })
      .catch(() => setStatus('denied'));
  }, []);

  if (status === 'loading') return null;
  if (status === 'unauthenticated') return <Navigate to="/" replace />;
  if (status === 'denied') return <NotFound />;
  return children;
};

const ProtectedEventosRoute = ({ children }) => {
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    const id = localStorage.getItem('idUsuario');
    if (!id) { setStatus('denied'); return; }
    const perfisRaw = localStorage.getItem('perfilUsuario');
    let perfis = [];
    try { perfis = JSON.parse(perfisRaw) || []; } catch {}
    if (perfis.some(p => ['admin', 'coordenador-geral'].includes(p))) { setStatus('ok'); return; }
    if (perfis.includes('coordenador-regional')) {
      RegioesService.getRegioes(true)
        .then(regioes => {
          const temEventos = Array.isArray(regioes) && regioes.some(
            r => r.nome && r.nome.trim().toLowerCase() === 'eventos'
          );
          setStatus(temEventos ? 'ok' : 'denied');
        })
        .catch(() => setStatus('denied'));
      return;
    }
    if (perfis.includes('secretaria')) {
      import('./service').then(({ default: Api }) => {
        Api.get(`pontos/listarPontos?coordenadorId=${id}`)
          .then(resp => {
            const pontos = Array.isArray(resp.data) ? resp.data : [];
            const tem = pontos.some(p => String(p?.nome || '').trim().toLowerCase() === 'eventos');
            setStatus(tem ? 'ok' : 'denied');
          })
          .catch(() => setStatus('denied'));
      });
      return;
    }
    setStatus('denied');
  }, []);
  if (status === 'loading') return null;
  if (status === 'denied') return <NotFound />;
  return children;
};

// ─── App ─────────────────────────────────────────────────────────────────────

const App = () => {
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) startTokenRefreshInterval();
  }, []);

  return (
  <Router>
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/home" element={<ProtectedRoute><ProtectedHomeRoute><HomePage /></ProtectedHomeRoute></ProtectedRoute>} />
      <Route path="/designacoes" element={<ProtectedRoute><Designacoes /></ProtectedRoute>} />
      <Route path="/eventos" element={<ProtectedRoute><ProtectedEventosRoute><Eventos /></ProtectedEventosRoute></ProtectedRoute>} />
      <Route path="/cadastrar" element={<ProtectedRoute><ProtectedCadastrar /></ProtectedRoute>}
      />
      <Route path="/publicadores" element={<ProtectedAdminRoute><ConsultarPublicadores /></ProtectedAdminRoute>} />
      <Route path="/gerenciar-publicadores" element={<ProtectedAdminRoute><GerenciarPublicadores/></ProtectedAdminRoute>} />
      <Route path="/experiencias" element={EXPERIENCIAS_ATIVO ? <ProtectedOnlyAdminRoute><Experiencias /></ProtectedOnlyAdminRoute> : <Navigate to="/home" replace />} />
      <Route path="/relatorios" element={<ProtectedAdminRoute><RelatorioDeUso /></ProtectedAdminRoute>} />
      <Route path="/gerenciar-pontos" element={<ProtectedAdminRoute><GerenciarPontos /></ProtectedAdminRoute>} />
      <Route path="/gerenciar-arranjos" element={<ProtectedAdminRoute><GerenciarArranjos /></ProtectedAdminRoute>} />
      <Route path="/notificacoes" element={<ProtectedNotificacoesRoute><Comunicacoes /></ProtectedNotificacoesRoute>} />
      {/* <Route path="/minhas-notificacoes" element={<ProtectedRoute><MinhasNotificacoes /></ProtectedRoute>} /> */}
      <Route path="/minhas-notificacoes" element={<Navigate to="/home" replace />} />
      <Route path="/comunicacoes-especiais" element={<ProtectedOnlyAdminRoute><ComunicacoesEspeciais /></ProtectedOnlyAdminRoute>} />
      {/* A lista de comunicados vive em /comunicacoes-especiais; /comunicados sozinho é alias */}
      <Route path="/comunicados" element={<Navigate to="/comunicacoes-especiais" replace />} />
      <Route path="/comunicados/criar" element={<ProtectedAdminRoute><FormComunicado /></ProtectedAdminRoute>} />
      <Route path="/comunicados/:id/resultados" element={<ProtectedAdminRoute><ResultadosComunicado /></ProtectedAdminRoute>} />
      <Route path="/suporte" element={<ProtectedAdminRoute><Suporte /></ProtectedAdminRoute>} />
      <Route path="/resetar-senhas" element={<GerenciarSenha />} />
      <Route path="/swagger" element={<ProtectedAdminRoute><SwaggerAdmin /></ProtectedAdminRoute>} />
      <Route path="/chamados" element={CHAMADOS_ATIVO ? <ProtectedRoute><ProtectedChamadosRoute><Chamados /></ProtectedChamadosRoute></ProtectedRoute> : <Navigate to="/home" replace />} />
      <Route path="/chamados/:id" element={CHAMADOS_ATIVO ? <ProtectedRoute><ProtectedChamadosRoute><ChamadoDetalhe /></ProtectedChamadosRoute></ProtectedRoute> : <Navigate to="/home" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Router>
);
};

const ProtectedCadastrar = () => {

  const [perfis, setPerfis] = useState(() => {
    try {
      const raw = localStorage.getItem('perfilUsuario');
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        if (parsed) return [parsed];
        return null;
      } catch (e) {
        return raw ? [raw] : null;
      }
    } catch (e) { return null; }
  });

  useEffect(() => {
    const id = localStorage.getItem('idUsuario');
    if (!id) {
      setPerfis([]);
      return;
    }
    UsuariosService.getUsuarioById(id)
      .then(u => {
        const arr = (u && Array.isArray(u.perfil)) ? u.perfil : (u && u.perfil ? [u.perfil] : []);
        setPerfis(arr);
        try { localStorage.setItem('perfilUsuario', JSON.stringify(u.perfil || [])); } catch (e) {}
      })
        .catch(() => setPerfis([]));
  }, []);

  if (perfis === null) return null;

  const perfisArr = Array.isArray(perfis) ? perfis : (perfis ? [perfis] : []);
  const podeCadastrar = perfisArr.some((p) => ['admin', 'colaborador', 'coordenador-geral', 'coordenador-regional'].includes(p));
  if (!podeCadastrar) return <NotFound />;

  return <CadastrarPublicador />;
};

export default App;
